import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { CheckCircle2, ArrowRight, Phone, Mail, MessageCircle, Quote } from "lucide-react";
import { motion } from "framer-motion";
import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import ppcHeroBg from "@assets/IMG_3138_1773677421535.webp";
import googlePartnerLogo from "@assets/image_1773657618812.webp";
import microsoftPartnerLogo from "@assets/image_1773657604580.webp";
import spoonerLogo from "@assets/spooner-logo_1776849762461_1776853870067.webp";
import uCheckLogo from "@assets/uCheck-150x150_1776849762462_1776854137017.webp";
import googleShoppingCaseStudy from "@assets/ppc_-_google_ads_account_data_1779180511353.jpg";

export default function PayPerClick() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const fadeIn = {
    initial: { opacity: 0, y: 20 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 }
  };

  const features = [
    {
      title: "Building the optimal strategy",
      description: "To build the right campaign for your business, our team of trained PPC experts will study your business, target audience, relevant search terms to your industry, and your competitors. Using this information, they can put together a robust, made-to-measure marketing strategy that is just right for your business."
    },
    {
      title: "Putting the plan into action",
      description: "Existing campaigns are refined, utilising new and relevant campaign types and strategies. Where necessary, a new structure may be put in place to ensure account performance is enhanced. All relevant conversions are tracked to provide reliable feedback on the account. If you don't have an existing PPC campaign, we will build the account from the ground up, inline with the current best practices. Our experience spans a multitude of industries."
    },
    {
      title: "Targeting keywords",
      description: "Keywords are the heart and soul of PPC. Finding the most relevant matches to your business and its target audience is critical to making your campaign effective. We identify keywords that will help your business stand out from the competition, excluding any irrelevant terms to reduce wastage."
    },
    {
      title: "PPC optimisation",
      description: "A well-optimised PPC campaign considers a lot of factors, but most importantly, it needs to meet your primary goals. Whether you are looking to maximise the amount of ad clicks you are getting, create increased visibility or achieve a greater ROI; we ensure the necessary changes and improvements are made to align with your goals."
    },
    {
      title: "Google Shopping",
      description: "Google Shopping ads are a great tool to promote items in your inventory and attract potential customers to your site. From setting up your Google Shopping ads to optimising the campaigns for higher conversion rates; our dedicated team of Google Shopping experts has years of experience, helping online businesses achieve sales targets that were previously thought to be unachievable!"
    },
    {
      title: "Powerful analytics",
      description: "Our pay-per-click management solutions not only put your brand out there, but they also provide powerful insights into how potential customers perceive you. This can help you understand how to outperform the competition."
    }
  ];

  const benefits = [
    "We analyse your company and your current competitors",
    "We determine the keywords that are appropriate to your products and services",
    "We closely monitor these keywords for in-depth analysis",
    "We get results"
  ];

  const testimonials = [
    {
      logo: spoonerLogo,
      quote: "I approached Page1 to help us increase traffic to our website through Adwords. Page 1 and particularly Tom were very helpful from the start keeping regular contact regarding the campaign and answering any questions we had promptly. Our Adwords campaign helped to increase the traffic to our website and we are now using Page 1 to emulate this with our aftersales website.",
      author: "Spooner"
    },
    {
      logo: uCheckLogo,
      quote: "We started working with Tom for our PPC in October 2014. Since this date our conversions have increased dramatically and we have been very happy with the performance of our campaign. Google AdWords can be a waste of money but with proper management from a reputable company it can be extremely beneficial.",
      author: "uCheck"
    }
  ];

  return (
    <div className="min-h-screen bg-background font-sans">
      <Helmet>
        <title>PPC Agency in Cornwall | The Cornwall SEO Co.</title>
        <meta name="description" content="With a proven track record, The Cornwall SEO Co. provides expert ppc management services to help meet your business goals." />
      </Helmet>
      <Navbar />
      <section 
        className="relative min-h-[70vh] flex items-center justify-center overflow-hidden pt-20 bg-black bg-cover bg-bottom"
        style={{ backgroundImage: `url(${ppcHeroBg})` }}
      >
        <div className="absolute inset-0 bg-black/60 z-0"></div>
        <div className="container mx-auto px-6 relative z-10">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="max-w-3xl"
          >
            <h1 className="text-5xl md:text-7xl font-heading font-black text-white mb-8 leading-none uppercase italic tracking-tighter">
              PPC Agency in <br/>
              <span className="text-primary">Cornwall</span>
            </h1>
            <p className="text-xl text-white/90 mb-10 leading-relaxed font-medium">
              Strong track record with proven results. At the Cornwall SEO Co., we offer pay-per-click management services that will help your business successfully utilise the power of paid marketing.
            </p>
            <a href="#contact-form" className="inline-block">
              <Button size="lg" className="bg-primary text-black hover:bg-white text-lg px-10 h-16 rounded-none font-black uppercase italic transition-all cursor-pointer">
                Contact Us <ArrowRight className="ml-2" />
              </Button>
            </a>
          </motion.div>
        </div>
      </section>

      {/* USP Section */}
      <section className="py-10 bg-black border-y border-primary/20">
        <div className="container mx-auto px-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-center divide-y md:divide-y-0 md:divide-x divide-primary/20">
            <div className="text-white font-black italic text-2xl py-4 md:py-0 px-4">100% CORNWALL-BASED TEAM</div>
            <div className="text-white font-black italic text-2xl py-4 md:py-0 px-4">NO MINIMUM TERM CONTRACT</div>
            <div className="text-white font-black italic text-2xl py-4 md:py-0 px-4">COMPETITIVE HOURLY RATE</div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <motion.div {...fadeIn} className="text-center mb-16">
            <h2 className="text-4xl md:text-6xl font-heading font-black text-black mb-6 uppercase italic tracking-tighter">
              Google Ads & <br/> <span className="text-primary">Microsoft Accredited</span>
            </h2>
            <p className="text-black text-xl font-medium max-w-2xl mx-auto">
              A certified Google Partner, The Cornwall SEO Co is also considered an Accredited Professional by Microsoft (Bing) Ads.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12 max-w-2xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="flex items-center justify-center"
            >
              <img src={googlePartnerLogo} alt="Google Partner" className="w-full h-auto object-contain" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="flex items-center justify-center"
            >
              <img src={microsoftPartnerLogo} alt="Microsoft Partner" className="w-full h-auto object-contain" />
            </motion.div>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <motion.div {...fadeIn} className="max-w-3xl mb-16">
            <h2 className="text-4xl md:text-6xl font-heading font-black text-black mb-6 uppercase italic tracking-tighter">
              Why Professional <br/> <span className="text-primary">PPC Management?</span>
            </h2>
            <p className="text-black text-xl font-medium leading-relaxed">
              While many may choose to try and run an advertising campaign themselves, this often leaves a lot of stones unturned that can lead to ineffective ads, high costs and wastage. Involving a professional, who knows the ins and outs of Google Ads management, will yield more cost-effective results.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-background">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <Card className="h-full rounded-none border-2 border-black p-6">
                  <CardHeader className="p-0 mb-4">
                    <CardTitle className="font-heading font-black text-xl md:text-xl text-black uppercase italic">{feature.title}</CardTitle>
                  </CardHeader>
                  <CardContent className="p-0">
                    <p className="text-black font-medium leading-relaxed text-lg md:text-sm">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-black text-white">
        <div className="container mx-auto px-6">
          <motion.div {...fadeIn} className="max-w-4xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-4xl md:text-6xl font-heading font-black uppercase italic tracking-tighter mb-4">
                Google Shopping: <span className="text-primary">Case Study</span>
              </h2>
              <p className="text-xl font-bold text-white/80 uppercase tracking-widest">Jan 1st – Apr 1st 2025</p>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-16">
              <div className="bg-white/10 p-6 text-center border-l-4 border-primary">
                <p className="text-white/70 font-bold uppercase text-sm mb-2">Ad Spend</p>
                <p className="text-3xl md:text-4xl font-black italic text-primary">£119K</p>
              </div>
              <div className="bg-white/10 p-6 text-center border-l-4 border-primary">
                <p className="text-white/70 font-bold uppercase text-sm mb-2">Conversions</p>
                <p className="text-3xl md:text-4xl font-black italic text-primary">12.5K</p>
              </div>
              <div className="bg-white/10 p-6 text-center border-l-4 border-primary">
                <p className="text-white/70 font-bold uppercase text-sm mb-2">Conversion Value</p>
                <p className="text-3xl md:text-4xl font-black italic text-primary">£1.19M</p>
              </div>
              <div className="bg-white/10 p-6 text-center border-l-4 border-primary">
                <p className="text-white/70 font-bold uppercase text-sm mb-2">ROAS</p>
                <p className="text-3xl md:text-4xl font-black italic text-primary">1002%</p>
              </div>
            </div>

            <div className="bg-primary text-black p-8 md:p-12 mb-16 text-center relative overflow-hidden">
              <div className="absolute -right-10 -top-10 text-[15rem] font-black italic opacity-10 leading-none">10x</div>
              <div className="relative z-10">
                <h3 className="text-3xl md:text-5xl font-black uppercase italic mb-8">10x Return</h3>
                <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-16">
                  <div>
                    <p className="font-bold text-lg mb-1 uppercase tracking-wide">Ad Spend</p>
                    <p className="text-2xl font-black italic">£119K (Investment)</p>
                  </div>
                  <ArrowRight className="hidden md:block w-12 h-12" />
                  <div>
                    <p className="font-bold text-lg mb-1 uppercase tracking-wide">Conversion Value</p>
                    <p className="text-2xl font-black italic">£1.19M (Return)</p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <h3 className="text-2xl font-black uppercase italic mb-6 text-center">Screenshot from Google Ads</h3>
              <div className="bg-white p-2 border-4 border-white/20">
                <img 
                  src={googleShoppingCaseStudy} 
                  alt="Google Ads Manager Screenshot showing 1002% ROAS" 
                  className="w-full h-auto object-cover"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-black text-white">
        <div className="container mx-auto px-6">
          <motion.div {...fadeIn} className="max-w-3xl mb-16">
            <h2 className="text-4xl md:text-6xl font-heading font-black text-primary mb-8 uppercase italic tracking-tighter">
              How Can The <br/> Cornwall SEO <br/> Co. Help?
            </h2>
            <p className="text-white/90 text-xl font-medium leading-relaxed mb-8">
              Although a simple advertising model, PPC management requires a meticulous approach to national/local searches for the campaign to be effective. We fully understand PPC platforms, from keyword planning and budget allocation to the actual implementation.
            </p>
          </motion.div>

          <div className="space-y-4 mb-12">
            {benefits.map((benefit, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="flex items-center gap-4 bg-primary/10 p-4 border-l-8 border-primary"
              >
                <CheckCircle2 className="text-primary shrink-0" size={24} />
                <span className="font-black uppercase italic text-white text-lg">{benefit}</span>
              </motion.div>
            ))}
          </div>

          <motion.p {...fadeIn} className="text-white/80 text-lg font-medium mb-8">
            With a strong track record, our PPC strategies are guaranteed to get you closer to your business goals. We can help to increase brand awareness, traffic, leads and sales based on your targets, reporting back on important metrics to show the campaign progress month on month.
          </motion.p>

          <motion.div {...fadeIn} className="flex items-center gap-6 text-xl font-black uppercase italic">
            <Phone size={24} className="text-primary" />
            <span>01726 74161</span>
          </motion.div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <motion.div {...fadeIn} className="max-w-3xl mb-16">
            <h2 className="text-4xl md:text-6xl font-heading font-black text-black mb-6 uppercase italic tracking-tighter">
              Client <span className="text-primary">Testimonials</span>
            </h2>
            <p className="text-black text-xl font-medium leading-relaxed">
              A few words from PPC clients we have helped drive stronger traffic and conversions.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-6">
            {testimonials.map((testimonial, index) => (
              <motion.article
                key={testimonial.author}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.08 }}
                className="bg-black text-white border-2 border-primary p-8 md:p-10"
              >
                <div className="flex items-center justify-between gap-8 mb-8">
                  <img src={testimonial.logo} alt={`${testimonial.author} logo`} className="h-20 md:h-24 w-auto max-w-[220px] object-contain bg-white p-3" />
                  <Quote className="text-primary shrink-0" size={34} />
                </div>
                <p className="text-white/85 text-base md:text-lg leading-relaxed italic mb-6">{testimonial.quote}</p>
                <p className="text-primary font-black uppercase italic tracking-widest text-sm">{testimonial.author}</p>
              </motion.article>
            ))}
          </div>
        </div>
      </section>

      <section id="contact-form" className="py-12 md:py-24 bg-primary">
        <div className="container mx-auto px-3 md:px-6">
          <div className="bg-black p-4 md:p-20 shadow-[20px_20px_0px_0px_rgba(255,255,255,0.3)] overflow-x-hidden">
            <div className="grid md:grid-cols-2 gap-4 md:gap-16">
              <div>
                <h3 className="text-3xl md:text-6xl font-heading font-black text-primary mb-4 md:mb-6 uppercase italic tracking-tighter">Ready to <br/> Scale Your <br/> Results?</h3>
                <p className="text-white/80 text-base md:text-xl mb-6 md:mb-12 font-medium">
                  For more information on how our pay-per-click management services can help your business, contact us
                </p>
                
                <div className="space-y-4 md:space-y-8">
                   <div className="flex items-center gap-3 md:gap-6">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-primary flex items-center justify-center shrink-0">
                         <Phone size={18} className="text-black md:w-6 md:h-6" />
                      </div>
                      <span className="text-white font-black uppercase italic text-sm md:text-xl break-words">01726 74161</span>
                   </div>
                   <div className="flex items-center gap-3 md:gap-6">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-primary flex items-center justify-center shrink-0">
                         <Mail size={18} className="text-black md:w-6 md:h-6" />
                      </div>
                      <a href="mailto:hello@thecornwallseoco.co.uk" className="text-white font-black uppercase italic text-xs md:text-xl hover:text-primary transition-colors break-words">hello@thecornwallseoco.co.uk</a>
                   </div>
                   <a
                     href="https://wa.me/447792646061"
                     target="_blank"
                     rel="noopener noreferrer"
                     className="flex items-center gap-3 md:gap-6 hover:text-primary transition-colors"
                   >
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-primary flex items-center justify-center shrink-0">
                         <MessageCircle size={18} className="text-black md:w-6 md:h-6" />
                      </div>
                      <span className="text-white font-black uppercase italic text-sm md:text-xl hover:text-primary transition-colors">WhatsApp</span>
                   </a>
                </div>
              </div>
              
              <form action="/contact.php" method="POST" className="space-y-3 md:space-y-4">
                <Input name="name" required placeholder="NAME" className="bg-white border-none rounded-none h-12 md:h-14 font-black text-sm md:text-base placeholder:text-black/30 w-full" />
                <Input name="email" required type="email" placeholder="EMAIL" className="bg-white border-none rounded-none h-12 md:h-14 font-black text-sm md:text-base placeholder:text-black/30 w-full" />
                <Input name="website" placeholder="WEBSITE" className="bg-white border-none rounded-none h-12 md:h-14 font-black text-sm md:text-base placeholder:text-black/30 w-full" />
                <Textarea name="message" required placeholder="HOW CAN WE HELP YOU?" className="bg-white border-none rounded-none h-24 md:h-32 font-black text-sm md:text-base placeholder:text-black/30 w-full" />
                <Button type="submit" className="w-full bg-primary text-black hover:bg-white h-14 md:h-16 font-black uppercase italic text-lg md:text-xl rounded-none transition-all cursor-pointer">
                  GO
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
